<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import {defineBreadcrumb, useDevice} from "#imports";
import type { IChapter } from "~/types/book";
import { ColorModes } from "~/types/base";
import useBook from "~/composible/useBook";
import QrcodeVue from 'qrcode.vue'
import { useAuth } from "~/composible/useAuth";
import useOneLink from "~/composible/useOneLink";

const { user, getOTT } = useAuth();
const { getAuthBookLink } = useOneLink();
const runtimeConfig = useRuntimeConfig();
const { slug, order } = useRoute().params;
const { getLinkToApp } = useBook();
const colorMode = useColorMode();
const { isMobile } = useDevice();
const { $toast } = useNuxtApp();

const orderNumber = ref(parseInt(order as string));
if (isNaN(orderNumber.value)) {
  orderNumber.value = 1;
}

if (orderNumber.value < 2) {
  await navigateTo(`/books/${slug}`)
}

const [chapterResponse, nextChaptersResponse, similarResponse] = await Promise.all([
  useApiFetch(`/novels/${slug}/chapters/${orderNumber.value}`),
  useApiFetch(`/novels/${slug}/chapters/${orderNumber.value}/next`),
  useApiFetch(`/novels/${slug}/similar`)
]);

const { data: chapterData, error: chapterError, status: bookStatus, refresh: refreshChapter } = chapterResponse;
const { data: nextChaptersData, error: nextChaptersError, status: NextChaptersStatus } = nextChaptersResponse;
const { data: similarData, status: similarStatus } = similarResponse;

if (chapterError.value?.statusCode === 404) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  });
}

const book = chapterData.value?.data.book;
const chapter: IChapter = computed(() => chapterData.value?.data.chapter);
const nextChapters: IChapter[] = nextChaptersData.value?.data;
const similar = ref(similarData.value?.data);

const isUploadedByUser = computed<boolean>(() => typeof book.uploaded_by === "string");

const prevChapterOrder = computed(() => chapter.value.order - 1);
const nextChapterOrder = computed(() => nextChapters[0] ? nextChapters[0].order : null);

const headPaginationLinks = computed(() => {
    let links = [];
    if (chapter.value.order >= 2) {
      const prevPath = prevChapterOrder.value < 2 ? `/books/${book.slug}` : `/books/${book.slug}/chapter-${prevChapterOrder.value}`;
      links.push({rel: 'prev', href: runtimeConfig.public.siteURL + prevPath})
    }
    if (nextChapterOrder.value) {
      const nextPath = `/books/${book.slug}/chapter-${nextChapterOrder.value}`;
      links.push({rel: 'next', href: runtimeConfig.public.siteURL + nextPath})
    }

    return links;
});

const genre = book?.genres ? book.genres[0] : null;

const chapterBlock = ref(null)
const showProscroll = ref(false);
const onPageScroll = () => {
  if (chapterBlock.value) {
    const position = chapterBlock.value.getBoundingClientRect();
    showProscroll.value = isMobile && position.top < 50 && position.bottom > 600;
  }
}

const appLinkLoading = ref(false);
const ott = ref<string|null>(null);
const uuid = ref<string|null>(user.value?.uuid || null);
const goApp = async () => {
  appLinkLoading.value = true;

  try {
    if (user.value) {
      const response = await getOTT();
      ott.value = response.data.token;
    }

    window.location.href = getAuthBookLink(book?.id, ott.value, uuid.value);
  } catch(error) {
    $toast.warning('Something went wrong.')
  } finally {
    appLinkLoading.value = false;
  }
}

const handleChapterUnlocked = () => {
  refreshChapter();
}

onMounted(() => {
  window.addEventListener('scroll', onPageScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onPageScroll);
})

const crumbs = ref(<Object[]>[]);
crumbs.value = [
  { link: '/genres/all-novels', label: 'Novels' },
  { link: `/genres/${genre?.slug}-novels`, label: genre?.name },
  { link: `/books/${book?.slug}`, label: book?.title },
  { label: chapter.value?.title }
];

const title = `${book.title} - ${chapter.value.title}`;
const description = `Read ${chapter.value.title} of ${book.title} on FictionMe`;

useHead({
  link: headPaginationLinks
});

useSeoMeta({
  titleTemplate: '%s – Read on FictionMe',
  title: title,
  description: description,
  ogTitle: title,
  ogDescription: description,
  twitterTitle: title,
  twitterDescription: description,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      defineListItem({
        position: 1,
        name: 'Novels',
        item: '/genres/all-novels'
      }),
      defineListItem({
        position: 2,
        name: genre?.name,
        item: `/genres/${genre?.slug}-novels`
      }),
      defineListItem({
        position: 3,
        name: book.title,
        item: `/books/${book.slug}`
      }),
      defineListItem({
        position: 4,
        name: chapter.value.title,
      })
    ]
  })
]);
</script>

<template>
  <div class="container py-8 max-md:py-6">
    <div class="page-content book-chapter flex flex-col gap-8 max-md:gap-6">
      <breadcrumbs :breadcrumbs="crumbs" />

      <div class="grid grid-cols-[1fr_176px] gap-8 max-lg:grid-cols-1">
        <div ref="chapterBlock">
          <h1 class="text-3xl font-bold mb-8 max-md:text-2xl">{{ chapter.title }}</h1>
          <reader
              v-if="chapter"
              :chapter="chapter"
              class="mb-8"
          />

          <chapter-unlocker
              v-if="!chapter.is_free && !chapter.is_purchased"
              :book-id="book.id"
              :chapter-id="chapter.id"
              :coins="chapter.coins"
              @chapter-unlocked="handleChapterUnlocked"
          />

          <div class="flex justify-center">
            <div class="flex gap-6 w-full max-w-lg max-md:gap-3 justify-center items-center">
              <router-link
                  v-if="chapter.order >= 2"
                  :to="prevChapterOrder < 2 ? `/books/${book.slug}` : `/books/${book.slug}/chapter-${prevChapterOrder}`"
                  :class="nextChapterOrder ? 'lg:flex-1' : ''"
                  class="flex items-center py-3 px-8 max-md:px-4 justify-center text-xl max-md:text-xl border-2 border-primary text-center rounded-xl text-violet-800 dark:text-violet-500 dark:hover:text-violet-600"
              >
                <svg class="w-5 h-5 lg:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                <span>Previous Chapter</span>
              </router-link>
              <router-link
                  v-if="nextChapterOrder"
                  :to="`/books/${book.slug}/chapter-${nextChapterOrder}`"
                  class="flex flex-1 items-center py-3 px-8 max-md:px-4 bg-fm-gradient hover:bg-fm-gradient-active text-center justify-center rounded-xl !text-white text-xl max-md:text-xl"
              >
                <span>Next Chapter</span>
                <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
        <div class="relative max-lg:hidden">
          <a
              :href="getLinkToApp(book.id, isUploadedByUser)"
              target="_blank"
              rel="nofollow"
              class="sticky flex gap-4 flex-col top-24 py-6 px-4 shadow hover:shadow-md rounded-xl dark:border-zinc-700 dark:bg-zinc-800 dark:border-[1px] items-center justify-center dark:!text-gray-200 !text-zinc-900"
          >
            <p class="text-center font-semibold">
              Scan the QR code to download FictionMe App and unlock more chapters!
            </p>

            <qrcode-vue
                class="text-primary"
                :value="getLinkToApp(book.id, isUploadedByUser)"
                :foreground="colorMode.preference === ColorModes.MODE_LIGHT ? '#6A2AD5' : '#A051FA'"
                background="transparent"
                level="M"
                render-as="svg"
            />
          </a>
        </div>
      </div>

      <lazy-f-card
          v-if="nextChapters.length > 0"
          title="Your Next Chapters"
      >
        <ul class="grid grid-cols-2 max-md:grid-cols-1 gap-x-4 chapter-list">
          <li v-for="chapter in nextChapters">
            <router-link
                :to="`/books/${book.slug}/chapter-${chapter.order}`"
                class="flex justify-between gap-1 rounded-xl"
            >
              <span class="truncate text-nowrap">{{ chapter.title }}</span>
              <icon v-if="!chapter.is_free && !chapter.is_purchased" name="fm:lock" class="text-gray-400" />
            </router-link>
          </li>
        </ul>
      </lazy-f-card>

      <lazy-f-card
          v-if="similar && similar.length > 0"
          title="You might like"
      >
        <div class="flex gap-5 max-md:gap-3 overflow-auto scrollbar-hidden">
          <book-card
              v-for="book in similar"
              :book="book"
              :key="book.id"
          />
        </div>
      </lazy-f-card>

      <lazy-cta-banner />

      <float-download-button
          :show="showProscroll"
          @clicked="goApp"
      />
    </div>
  </div>
</template>

<style scoped>
.chapter-list > li > a {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  @apply px-2 py-4 rounded-lg
}

.chapter-list > li:nth-child(4n+1) > a,
.chapter-list > li:nth-child(4n+2) > a {
  background-color: white;
  @apply dark:bg-zinc-950
}

.chapter-list > li:nth-child(4n+3) > a,
.chapter-list > li:nth-child(4n+4) > a {
  background-color: #F4F4F5;
  @apply dark:bg-zinc-800
}
</style>